import { useFetch } from "~vue/api";
import { useDeferredLoading } from "~vue/composables/useDeferredLoading";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { computed, type MaybeRefOrGetter, ref, toValue } from "vue";

type UseLinkPublicProfileOptions = {
    connectionId: MaybeRefOrGetter<string>;
    source: "chat" | "my_people";
    coachingSessionUuid?: string;
    onLinked?: () => void;
};

export function useLinkPublicProfile({ connectionId, source, coachingSessionUuid, onLinked }: UseLinkPublicProfileOptions) {
    const linkUrl = computed(() => `/api/connections/${toValue(connectionId)}/`);
    const payload = ref<{ public_profile: string | null }>({ public_profile: null });

    const { execute, error, isFetching: isLinking } = useFetch(linkUrl, { immediate: false }).put(payload).json();

    const { loading: isDeferredLinking } = useDeferredLoading({ threshold: 500, loadingState: isLinking });

    const linkProfile = async (profileUserId: string) => {
        try {
            void logUserInteraction(
                AmplitudeEventNames.CONNECTION_LINK_PUBLIC_PROFILE,
                {
                    connection_id: toValue(connectionId),
                    profile_user_id: profileUserId,
                    source,
                },
                coachingSessionUuid,
            );

            payload.value = { public_profile: profileUserId };
            await execute();

            if (error.value) {
                logError(`Failed to link collaboration profile ${error.value}`);
                return;
            }

            onLinked?.();
        } catch (e) {
            logError(`Link profile error ${e}`);
        }
    };

    return { linkProfile, isLinking, isDeferredLinking, error };
}
